import React, { useState } from 'react';
import { WizardStep } from '../components/WizardStep';
import { QuestionCard } from '../components/QuestionCard';
import { RiskProfileResult } from '../components/RiskProfileResult';
import { DominionLogo } from '../components/DominionLogo';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';
import { useImageCache } from '../hooks/useImageCache';

interface Answers {
  q1: string;
  q2: string;
  q3: string;
  q4: string;
  q5: string;
  q6: string;
  [key: string]: string;
}

export default function Home() {
  const { translations: t } = useLanguage();
  
  // OPTIMIZATION: Pre-load fund logos in background when app mounts
  useImageCache();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({
    q1: '',
    q2: '',
    q3: '',
    q4: '',
    q5: '',
    q6: '',
  });
  const [showResults, setShowResults] = useState(false);

  const totalSteps = t.questions.length + 2; // +2 for intro and results

  const handleAnswer = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep === t.questions.length) {
      setShowResults(true);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  const calculateScore = (): number => {
    return Object.values(answers).reduce((sum, value) => sum + parseInt(value || '0'), 0);
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({
      q1: '',
      q2: '',
      q3: '',
      q4: '',
      q5: '',
      q6: '',
    });
    setShowResults(false);
  };

  const canGoNext = () => {
    if (currentStep === 0) return true;
    const questionIndex = currentStep - 1;
    const questionId = t.questions[questionIndex]?.id;
    return answers[questionId as keyof Answers] !== '';
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // IGNORE keyboard shortcuts if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable
      ) {
        return; // Don't handle keyboard shortcuts when typing
      }

      // Handle Enter key on welcome screen (step 0)
      if (e.key === 'Enter' && currentStep === 0 && !showResults) {
        e.preventDefault();
        if (canGoNext()) {
          handleNext();
        }
      }
      
      // Handle Backspace/Delete on results page
      if ((e.key === 'Backspace' || e.key === 'Delete') && showResults) {
        e.preventDefault();
        setShowResults(false);
        setCurrentStep(t.questions.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, showResults, canGoNext, handleNext, t.questions.length]);

  return (
    <div className="min-h-screen bg-[#0d0d12] relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-[#90d977]/20 to-transparent blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#90d977]/10 to-transparent blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 md:py-12 max-w-4xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 md:mb-12">
          <div className="flex-1 hidden md:block" />
          <div className="flex justify-start md:justify-center items-center flex-1">
            <DominionLogo className="h-6 md:h-8 w-auto" />
          </div>
          <div className="flex-1 flex justify-end">
            <LanguageSelector />
          </div>
        </div>

        {/* Wizard Steps */}
        {!showResults && currentStep === 0 && (
          <WizardStep
            currentStep={1}
            totalSteps={totalSteps}
            onNext={handleNext}
            isFirstStep
            canGoNext
          >
            <div>
              <h2 className="text-2xl md:text-3xl font-medium text-white text-center mb-6">
                {t.welcome.title}
              </h2>
              <p className="text-white text-center mb-6">
                {t.welcome.description}
              </p>
              <div className="bg-[#0d0d12] border border-[#27272a] rounded-lg p-6 mb-4">
                <h3 className="text-white mb-4 text-left">{t.welcome.whatWeEvaluate}</h3>
                  <ul className="space-y-3 text-sm text-left">
                    {t.welcome.items.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-[#90d977] mr-2">•</span>
                        <span className="text-white">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              <p className="text-sm text-[#a1a1aa] text-center">
                {t.welcome.duration}
              </p>
              {/* Keyboard hint - Hidden on mobile */}
              <div className="hidden md:block text-center text-sm text-[#a1a1aa] mt-6">
                {t.welcome.keyboardHint.split('{enter}')[0]}
                <kbd className="px-2 py-1 bg-[#18181b] border border-[#3f3f46] rounded text-[#90d977] mx-1">Enter</kbd>
                {t.welcome.keyboardHint.split('{enter}')[1]}
              </div>
            </div>
          </WizardStep>
        )}

        {!showResults && currentStep > 0 && currentStep <= t.questions.length && (
          <WizardStep
            currentStep={currentStep + 1}
            totalSteps={totalSteps}
            onNext={handleNext}
            onPrev={handlePrev}
            canGoNext={canGoNext()}
          >
            <QuestionCard
              question={t.questions[currentStep - 1].question}
              options={t.questions[currentStep - 1].options}
              value={answers[t.questions[currentStep - 1].id as keyof Answers]}
              onChange={(value) => handleAnswer(t.questions[currentStep - 1].id, value)}
              onNext={handleNext}
              onPrev={handlePrev}
            />
          </WizardStep>
        )}

        {showResults && (
          <WizardStep
            currentStep={totalSteps}
            totalSteps={totalSteps}
            onPrev={() => {
              setShowResults(false);
              setCurrentStep(t.questions.length);
            }}
            isLastStep
          >
            <RiskProfileResult
              score={calculateScore()}
              answers={answers}
              onRestart={handleRestart}
            />
          </WizardStep>
        )}
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center pb-8 text-[#a1a1aa] text-sm">
        <p>{t.header.poweredBy}</p>
      </div>
    </div>
  );
}
